import { Box } from "@mui/material";
import { Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper";
import PrePaidCard from "../../../reusable-components/cards/pre-paid-card/PrePaidCard";
import PhCaretRightSvg from "../../../../assets/svg-fc/PhCaretRightSvg";

import "swiper/css";
import { EventRequestStatusEnum, IRequest } from "../../../../types/eventTypes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/storeHooks";
import { useToast } from "../../../../context/ToasterContext";
import { acceptEventRequestThunk, declineEventRequestThunk } from "../../../../store/events/eventThunk";
import { resetApiErrorEvent } from "../../../../store/events/eventSlice";
import ShopifyDeclineModal from "../../../reusable-components/modals/shopify-decline-modal/ShopifyDeclineModal";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ROUTE_QUERY_CONSTANTS } from "../../../../routes";
interface PrePaidSliderProps {
	eventRequests: IRequest[];
}
const PrePaidSlider = ({ eventRequests }: PrePaidSliderProps) => {
	const [shopifyDeclineModalState, setShopifyDeclineModalState] = useState(false);

	const [declineRequestId, setDeclineRequestId] = useState("");
	const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
	const [paging, setPaging] = useState({ page: 1, pages: 1, atStart: true, atEnd: true });
	const updatePaging = useCallback((instance: SwiperInstance) => {
		const pages = Math.max(1, instance.snapGrid.length);
		setPaging({
			page: Math.min(pages, instance.snapIndex + 1),
			pages,
			atStart: instance.isBeginning,
			atEnd: instance.isEnd,
		});
	}, []);

	const apiError = useAppSelector((state) => state.events.apiError);
	const apiSuccess = useAppSelector((state) => state.events.apiSuccess);
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const handleDeclineClick = useCallback(async (eventRequestId: string) => {
		setShopifyDeclineModalState(true);
		setDeclineRequestId(eventRequestId);
	}, []);

	const handleAcceptClick = useCallback(async (eventRequestId: string) => {
		dispatch(acceptEventRequestThunk(eventRequestId));
	}, []);

	useEffect(() => {
		if (apiError) {
			showToast(apiError.message, () => onToasterClose(), "error");
		}
		if (apiSuccess) {
			showToast(apiSuccess.message, () => onToasterClose(), "success");
		}
	}, [apiError, apiSuccess]);
	const onToasterClose = () => {
		dispatch(resetApiErrorEvent());
	};
	return (
		<Box
			sx={{
				"& .swiper-slide": {
					height: "auto",
				},
			}}
		>
			<Swiper
				spaceBetween={12}
				slidesPerView={1}
				onSwiper={(instance) => {
					setSwiper(instance);
					updatePaging(instance);
				}}
				onSlideChange={updatePaging}
				onSnapGridLengthChange={updatePaging}
				onResize={updatePaging}
				onReachBeginning={updatePaging}
				onReachEnd={updatePaging}
				grabCursor
				mousewheel={{ forceToAxis: true }}
				modules={[Mousewheel]}
				breakpoints={{
					768: {
						slidesPerView: 2,
					},
					1200: {
						slidesPerView: 3,
					},
				}}
			>
				{eventRequests.map((eventRequest: IRequest) => {
					return (
						<SwiperSlide key={eventRequest.id}>
							<PrePaidCard
								key={eventRequest.id}
								eventRequestId={eventRequest.id}
								title={eventRequest.name}
								declinedClick={(e) => {
									e.stopPropagation();
									handleDeclineClick(eventRequest.id);
								}}
								completedClick={(e) => {
									e.stopPropagation();
									handleAcceptClick(eventRequest.id);
								}}
								date={moment(eventRequest.eventDate).format("DD MMMM YYYY")}
								guestsCounter={eventRequest.guestCount + ""}
								AED={(eventRequest.price.toFixed(2) || 0) + ""}
								expireAt={eventRequest.expireAt}
								openCardDetail={() => {
									navigate(
										`/event-detail/${
											eventRequest?.event?.id || ""
										}?type=${ROUTE_QUERY_CONSTANTS.AiDirectRequest}`,
									);
								}}
								onHold={eventRequest.status === EventRequestStatusEnum.OnHold}
								startTime={eventRequest.startTime}
								customerNotes={eventRequest?.payload?.note ?? ""}
							/>
						</SwiperSlide>
					);
				})}
			</Swiper>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					gap: 2,
					mt: 3,
				}}
			>
				{(["prev", "next"] as const).map((direction) => (
					<Box
						key={direction}
						component="button"
						type="button"
						disabled={direction === "prev" ? paging.atStart : paging.atEnd}
						onClick={() => (direction === "prev" ? swiper?.slidePrev() : swiper?.slideNext())}
						aria-label={direction === "prev" ? "Previous" : "Next"}
						sx={{
							order: direction === "prev" ? 0 : 2,
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							p: 0,
							borderRadius: "50%",
							border: "1px solid #d0d5dd",
							bgcolor: "#fff",
							color: "#22031f",
							cursor: "pointer",
							"& svg": { transform: direction === "prev" ? "scaleX(-1)" : "none" },
							"&:disabled": { opacity: 0.4, cursor: "default" },
						}}
					>
						<PhCaretRightSvg size={12} />
					</Box>
				))}
				<Box
					component="span"
					sx={{
						order: 1,
						minWidth: 48,
						textAlign: "center",
						fontWeight: 500,
						color: "#22031f",
					}}
				>
					{`${paging.page} / ${paging.pages}`}
				</Box>
			</Box>

			<ShopifyDeclineModal
				modalHeaderText="Are you sure you want to Decline?"
				open={shopifyDeclineModalState}
				onClose={() => {
					setShopifyDeclineModalState(false);
					setDeclineRequestId("");
				}}
				shopifyDeclineModalBodyText="Are you sure you want to decline this event? This action can not be undone"
				declineOnClick={async () => {
					await dispatch(declineEventRequestThunk(declineRequestId));
					setShopifyDeclineModalState(false);
				}}
				backOnClick={() => {
					setShopifyDeclineModalState(false);
					setDeclineRequestId("");
				}}
			/>
		</Box>
	);
};

export default PrePaidSlider;
